// «Собрать с ИИ» на экране «Тренировка»: на что, сколько минут, интенсивность, оборудование и
// пожелания → черновик одной тренировки (сервер ничего не сохраняет) → превью → «Открыть»
// (тренировка готова, идёт с «Старт»).
// Упражнение не из справочника заменяется похожим или убирается прямо в превью.
// Выбор фокуса, времени, интенсивности и оборудования запоминается на устройстве.

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { Button, Card, ErrorText, Select, Sheet, controlClass } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  useAiStatus,
  useProposeWorkout,
  type Equipment,
  type WorkoutDraft,
  type WorkoutFocus,
  type WorkoutIntensity,
  type WorkoutRequest,
} from '@/pages/ai/api'
import { EQUIPMENT_LABELS, exerciseTargets, matchNote } from '@/pages/ai/logic'
import { Disclaimer, Markdown } from '@/pages/ai/parts'

const FOCUS_LABELS: Record<WorkoutFocus, string> = {
  chest: 'Грудь',
  back: 'Спина',
  shoulders: 'Плечи',
  arms: 'Руки',
  legs: 'Ноги',
  glutes: 'Ягодицы',
  core: 'Пресс',
  full_body: 'Всё тело',
  cardio: 'Кардио',
}

const INTENSITY_LABELS: Record<WorkoutIntensity, string> = { light: 'Лёгкая', moderate: 'Средняя', hard: 'Тяжёлая' }

const MINUTES = [20, 30, 45, 60, 75, 90].map((n) => ({ value: String(n), label: `${n} мин` }))

const STORE_KEY = 'telos.aiWorkout'

type Saved = Omit<WorkoutRequest, 'notes'>

const DEFAULTS: Saved = { focus: [], minutes: 45, intensity: 'moderate', equipment: [] }

function loadSaved(): Saved {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Saved>) } : DEFAULTS
  } catch {
    return DEFAULTS
  }
}

function save(value: Saved) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(value))
  } catch {
    // приватный режим — просто не запомним
  }
}

function toggled<T>(list: T[], key: T): T[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key]
}

function Chips<T extends string>({
  legend,
  labels,
  value,
  onToggle,
  hint,
}: {
  legend: string
  labels: Record<T, string>
  value: T[]
  onToggle: (key: T) => void
  hint?: string
}) {
  return (
    <fieldset className="grid gap-2">
      <legend className="text-sm font-medium text-fg">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(labels) as T[]).map((key) => (
          <Button
            key={key}
            type="button"
            size="sm"
            variant={value.includes(key) ? 'primary' : 'secondary'}
            aria-pressed={value.includes(key)}
            onClick={() => onToggle(key)}
          >
            {labels[key]}
          </Button>
        ))}
      </div>
      {hint ? <p className="text-sm text-muted">{hint}</p> : null}
    </fieldset>
  )
}

function Preview({ draft, onChange }: { draft: WorkoutDraft; onChange: (d: WorkoutDraft) => void }) {
  const replace = (index: number, id: string, name: string, tracking: string) =>
    onChange({
      ...draft,
      exercises: draft.exercises.map((e, i) =>
        i === index ? { ...e, exercise_id: id, exercise_name: name, tracking, suggestions: [] } : e,
      ),
    })
  const remove = (index: number) => onChange({ ...draft, exercises: draft.exercises.filter((_, i) => i !== index) })

  return (
    <div className="grid gap-4">
      <h3 className="text-lg font-semibold text-fg">{draft.title}</h3>
      {draft.summary ? <Markdown text={draft.summary} /> : null}
      <ol className="grid gap-2">
        {draft.exercises.map((ex, i) => (
          <li
            key={`${i}-${ex.name}`}
            className={cn(
              'grid gap-1 rounded-lg border p-3',
              ex.exercise_id ? 'border-border' : 'border-warning/40 bg-warning-soft/40',
            )}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="font-medium text-fg">
                {i + 1}. {ex.exercise_name ?? ex.name}
              </span>
              <span className="text-sm tabular-nums text-muted">{exerciseTargets(ex)}</span>
            </div>
            {matchNote(ex) ? <p className="text-xs text-muted">{matchNote(ex)}</p> : null}
            {ex.notes ? <p className="text-sm text-muted">{ex.notes}</p> : null}
            {ex.exercise_id ? null : (
              <div className="grid gap-2">
                <p className="text-sm text-fg">Такого упражнения нет в справочнике — замени похожим или убери.</p>
                <div className="flex flex-wrap gap-2">
                  {ex.suggestions.map((s) => (
                    <Button key={s.id} size="sm" onClick={() => replace(i, s.id, s.name, s.tracking)}>
                      {`Заменить на «${s.name}»`}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => remove(i)}>
                    Убрать
                  </Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ol>
      <Disclaimer />
    </div>
  )
}

export function AiWorkoutCard({ busy, onStart }: { busy: boolean; onStart: (draft: WorkoutDraft) => void }) {
  const status = useAiStatus()
  const propose = useProposeWorkout()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<Saved>(loadSaved)
  const [notes, setNotes] = useState('')
  const [draft, setDraft] = useState<WorkoutDraft | null>(null)
  const configured = status.data?.configured === true

  const update = (patch: Partial<Saved>) =>
    setForm((prev) => {
      const next = { ...prev, ...patch }
      save(next)
      return next
    })

  const submit = (event?: FormEvent) => {
    event?.preventDefault()
    propose.mutate({ ...form, notes: notes.trim() || null }, { onSuccess: setDraft })
  }

  const unresolved = draft?.exercises.some((e) => e.exercise_id === null) ?? false
  const empty = (draft?.exercises.length ?? 0) === 0

  const footer = draft ? (
    <>
      <Button variant="ghost" onClick={() => setDraft(null)}>
        Изменить запрос
      </Button>
      <Button icon="refresh" loading={propose.isPending} onClick={() => submit()}>
        Собрать заново
      </Button>
      <Button
        variant="primary"
        disabled={busy || unresolved || empty}
        onClick={() => {
          onStart(draft)
          setOpen(false)
        }}
      >
        Открыть тренировку
      </Button>
    </>
  ) : (
    <>
      <Button onClick={() => setOpen(false)}>Закрыть</Button>
      <Button
        type="submit"
        form="ai-workout-form"
        variant="primary"
        icon="ai"
        loading={propose.isPending}
        disabled={form.focus.length === 0}
      >
        Собрать
      </Button>
    </>
  )

  return (
    <Card title="С ИИ" subtitle="Скажи, на что и сколько времени, — ИИ соберёт тренировку на сегодня с учётом самочувствия и прошлых дней.">
      {status.isSuccess && !configured ? (
        <p className="text-sm text-muted">
          ИИ не настроен —{' '}
          <Link to="/settings/ai" className="text-accent hover:underline">
            настроить
          </Link>
          .
        </p>
      ) : (
        <Button size="lg" block icon="ai" disabled={busy || !configured} onClick={() => setOpen(true)}>
          Собрать тренировку с ИИ
        </Button>
      )}
      <Sheet open={open} onClose={() => setOpen(false)} title="Тренировка с ИИ" fullMobile wide footer={footer}>
        {draft ? (
          <>
            <Preview draft={draft} onChange={setDraft} />
            {propose.isError ? <ErrorText error={propose.error} className="mt-3" /> : null}
          </>
        ) : (
          <form id="ai-workout-form" className="grid gap-4" onSubmit={submit}>
            <Chips
              legend="На что"
              labels={FOCUS_LABELS}
              value={form.focus}
              onToggle={(key) => update({ focus: toggled(form.focus, key) })}
              hint={form.focus.length === 0 ? 'Выбери хотя бы одно.' : undefined}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Время"
                value={String(form.minutes)}
                onChange={(e) => update({ minutes: Number(e.target.value) })}
                options={MINUTES}
              />
              <fieldset className="grid gap-1.5">
                <legend className="text-sm font-medium text-fg">Интенсивность</legend>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(INTENSITY_LABELS) as WorkoutIntensity[]).map((key) => (
                    <Button
                      key={key}
                      type="button"
                      size="sm"
                      className="flex-1"
                      variant={form.intensity === key ? 'primary' : 'secondary'}
                      aria-pressed={form.intensity === key}
                      onClick={() => update({ intensity: key })}
                    >
                      {INTENSITY_LABELS[key]}
                    </Button>
                  ))}
                </div>
              </fieldset>
            </div>
            <Chips
              legend="Оборудование"
              labels={EQUIPMENT_LABELS as Record<Equipment, string>}
              value={form.equipment}
              onToggle={(key) => update({ equipment: toggled(form.equipment, key) })}
              hint="Ничего не выбрано — подойдёт любое."
            />
            <div className="grid gap-1.5">
              <label htmlFor="ai-workout-notes" className="text-sm font-medium text-fg">
                Пожелания
              </label>
              <textarea
                id="ai-workout-notes"
                rows={3}
                maxLength={1000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Например: хочу попробовать маятниковые приседания, без становой, плечо побаливает"
                className={cn(controlClass, 'h-auto min-h-20 resize-y py-2')}
              />
            </div>
            {propose.isError ? <ErrorText error={propose.error} /> : null}
            {propose.isPending ? <p className="text-sm text-muted">ИИ собирает тренировку — это может занять до минуты.</p> : null}
          </form>
        )}
      </Sheet>
    </Card>
  )
}
